/**
 * Maintenance utility functions
 * Centralized date formatting, severity sorting, and other helpers
 */

// ============================================================================
// SEVERITY CONSTANTS & SORTING
// ============================================================================

export const MAINTENANCE_SEVERITY_NAMES = [
  'Emergency',
  'Very High',
  'High',
  'Medium',
  'Normal',
  'Low',
  'Very Low',
];

/**
 * Sort severity names by priority (Emergency first, Very Low last)
 */
export const sortMaintenanceSeverityNames = (a: string, b: string) =>
  MAINTENANCE_SEVERITY_NAMES.indexOf(a) - MAINTENANCE_SEVERITY_NAMES.indexOf(b);

// ============================================================================
// DATE FORMATTING
// ============================================================================

/**
 * Date format options for different display contexts
 */
export type DateFormatType = 
  | 'short'      // "Dec 8"
  | 'medium'     // "Dec 8, 2025"
  | 'long'       // "Dec 8, 2025, 3:45 PM"
  | 'relative';  // Uses formatRelativeDate from common utils

/**
 * Format a date string for display
 * Consolidates all date formatting for maintenance tickets
 * 
 * @param dateStr - ISO date string or undefined
 * @param format - Format type: 'short', 'medium', 'long'
 * @param fallback - Fallback value if date is invalid (default: '-')
 * @returns Formatted date string
 * 
 * @example
 * formatTicketDate('2025-12-08T15:45:00Z', 'short')   // "Dec 8"
 * formatTicketDate('2025-12-08T15:45:00Z', 'medium')  // "Dec 8, 2025"
 * formatTicketDate('2025-12-08T15:45:00Z', 'long')    // "Dec 8, 2025, 3:45 PM"
 */
export const formatTicketDate = (
  dateStr?: string,
  format: Exclude<DateFormatType, 'relative'> = 'medium',
  fallback = '-'
): string => {
  if (!dateStr) return fallback;
  
  try {
    const date = new Date(dateStr);
    
    // Check for invalid date
    if (isNaN(date.getTime())) {
      return fallback;
    }
    
    switch (format) {
      case 'short':
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
        
      case 'medium':
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
        
      case 'long':
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        
      default:
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
    }
  } catch {
    return fallback;
  }
};

/**
 * Format month and day only (e.g., "Dec 8")
 * Alias for formatTicketDate with 'short' format
 * @deprecated Use formatTicketDate(dateStr, 'short') instead
 */
export const formatMonthDay = (dateStr?: string): string => {
  return formatTicketDate(dateStr, 'short', '');
};

/**
 * Format date with time (e.g., "Dec 8, 2025, 3:45 PM")
 * Alias for formatTicketDate with 'long' format
 */
export const formatDateWithTime = (dateStr?: string, fallback = '-'): string => {
  return formatTicketDate(dateStr, 'long', fallback);
};

/**
 * Format date without time (e.g., "Dec 8, 2025")
 * Alias for formatTicketDate with 'medium' format
 */
export const formatDateOnly = (dateStr?: string, fallback = '-'): string => {
  return formatTicketDate(dateStr, 'medium', fallback);
};

// ============================================================================
// MAINTENANCE HOURS FORMATTING
// ============================================================================

/**
 * Format maintenance hours into iOS-like rows: "Monday 8 AM - 4 PM".
 * - Accepts arbitrary keys ("mon", "Mon", "monday", etc.)
 * - Orders output Monday → Sunday; unknown keys appended after
 * - If both start/end are missing or closed=true, shows "Closed"
 */
export const formatMaintenanceHoursList = (
  maintenanceHours?: Record<string, { start?: string; end?: string; closed?: boolean }>
): Array<{ dayLabel: string; timeLabel: string }> => {
  const keys = Object.keys(maintenanceHours || {});
  if (!keys.length) return [];

  const preferredOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const findKey = (target: string) =>
    keys.find(k => k.toLowerCase() === target.toLowerCase())
    || keys.find(k => k.toLowerCase().startsWith(target.slice(0, 3).toLowerCase()));

  const orderedKeys: string[] = [];
  preferredOrder.forEach(day => {
    const m = findKey(day);
    if (m && !orderedKeys.includes(m)) orderedKeys.push(m);
  });
  keys.forEach(k => {
    if (!orderedKeys.includes(k)) orderedKeys.push(k);
  });

  const fullDayName = (key: string): string => {
    const lower = key.toLowerCase();
    if (lower.startsWith('mon')) return 'Monday';
    if (lower.startsWith('tue')) return 'Tuesday';
    if (lower.startsWith('wed')) return 'Wednesday';
    if (lower.startsWith('thu')) return 'Thursday';
    if (lower.startsWith('fri')) return 'Friday';
    if (lower.startsWith('sat')) return 'Saturday';
    if (lower.startsWith('sun')) return 'Sunday';
    return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatHour = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
  };

  return orderedKeys.map((dayKey) => {
    const hours = (maintenanceHours as any)[dayKey] || {};
    const start = hours?.start as (string | undefined);
    const end = hours?.end as (string | undefined);
    const isClosed = (!start && !end) || hours?.closed === true;
    const timeLabel = isClosed ? 'Closed' : `${formatHour(start)} - ${formatHour(end)}`;
    return { dayLabel: fullDayName(dayKey), timeLabel };
  });
};

// ============================================================================
// CATEGORY & SUBCATEGORY UTILITIES
// ============================================================================

/**
 * Get all subcategories flattened from all categories
 * Used in mobile/simplified flows where user selects subcategory without first selecting category
 * 
 * @param maintenanceInfo - Maintenance info response containing categories
 * @returns Array of unique subcategory names
 * 
 * @example
 * const allSubs = getAllSubCategories(maintenanceInfo);
 * // ["Plumbing", "Electrical", "HVAC", ...]
 */
export const getAllSubCategories = (
  maintenanceInfo?: { categories?: Array<{ subCategories?: Array<{ name: string }> }> }
): string[] => {
  if (!maintenanceInfo?.categories?.length) return [];
  const allSubs: string[] = [];
  maintenanceInfo.categories.forEach(cat => {
    cat.subCategories?.forEach(sub => {
      if (!allSubs.includes(sub.name)) {
        allSubs.push(sub.name);
      }
    });
  });
  return allSubs;
};

/**
 * Find parent category for a given subcategory name
 * Used when user selects a subcategory but we need to submit the parent category to the API
 * 
 * @param maintenanceInfo - Maintenance info response containing categories
 * @param subCategoryName - Name of the subcategory to find parent for
 * @returns Parent category name, or empty string if not found
 * 
 * @example
 * const category = findCategoryForSubCategory(maintenanceInfo, "Plumbing");
 * // "Maintenance"
 */
export const findCategoryForSubCategory = (
  maintenanceInfo: { categories?: Array<{ name: string; subCategories?: Array<{ name: string }> }> } | undefined,
  subCategoryName: string
): string => {
  if (!maintenanceInfo?.categories?.length || !subCategoryName) return '';
  for (const cat of maintenanceInfo.categories) {
    if (cat.subCategories?.some(sub => sub.name === subCategoryName)) {
      return cat.name;
    }
  }
  return '';
};


